import { Input, List, InputGroup, Loader } from 'rsuite'
import { ActionButton } from '@/components/styles'
import useApi from '@/hooks/useApi'
import { useState, useRef, useEffect } from 'react'
import useLocalStorage from '@/hooks/useLocalStorage'
import qs from 'qs'
import { BsSearch } from 'react-icons/bs'
import DirectionalArrow from '@/components/themes/common/icons/directionalArrow'
import { FaVideo } from 'react-icons/fa'
import { GrDocumentPdf } from 'react-icons/gr'
import useTranslation from '@/hooks/useTranslation'
import styles from './searchByKeyword.module.scss'
import SearchByKeywordHistory from '@/components/publications/searchByKeywordHistory'
import cn from 'classnames'
import VideoModal from '@/components/videoModal'
import useVideoModal from '@/hooks/useVideoModal'
import { useRouter } from 'next/router'
import SubaruPublicationTypeIcon from '@/components/themes/subaru/customIcon'
import { useSelector } from 'react-redux'
import { configSelectors } from '@/store/configs'
import { publicationHelper } from '@tweddle-se-team/common/helpers'
import sanitizeHtml from 'sanitize-html'
import IcoMoonIcon from '@/components/themes/common/icons/IcoMoonIcon'
import ICON_MAP from '@/components/themes/common/icons/iconMapper'
import { customerId } from '@/utils/env'
import { usePublicationLabel } from '@/hooks/usePublicationLabel'
import { uniq } from 'lodash'

const { generatePartNumber } = publicationHelper

const SEARCH_HISTORY_KEY = 'searchByKeyword_history'

const allowedTags = sanitizeHtml.defaults.allowedTags.concat(['img'])
const allowedAttributes = {
  img: ['src', 'alt', 'title'],
  a: ['href', 'target', 'rel'],
  span: ['class']
}
const sanitize = html => sanitizeHtml(html, { allowedTags, allowedAttributes })

const SearchByKeywordNordics = ({
  brand,
  model,
  modelType,
  year,
  language,
  closeWrapper,
  fullReloadOnSelectTopic = true,
  onSearchComplete,
  cachedSearch,
  publicationsToSearch
}) => {
  const [keyword, setKeyword] = useState(cachedSearch?.keyword || '')
  const [result, setResult] = useState(cachedSearch?.result ?? null)
  const [loading, setLoading] = useState(false)
  const [activeItem, setActiveItem] = useState(null)
  const [showHistory, setShowHistory] = useState(false)
  const [history, setHistory, clearHistory] = useLocalStorage(SEARCH_HISTORY_KEY, [])
  const searchInputRef = useRef(null)
  const searchContainerRef = useRef(null)
  const prevYearRef = useRef(year)
  const router = useRouter()
  const apiHook = useApi()
  const t = useTranslation()
  const { videoTitle, videoUrl, openVideoModal, closeVideoModal, isVideoModalOpen } = useVideoModal()
  const publicationTypes = useSelector(configSelectors.selectConfigByKey('publicationTypes'))

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = event => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowHistory(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (prevYearRef.current !== year) {
      prevYearRef.current = year
      setKeyword('')
      setResult(null)
    }
  }, [year])

  const updateHistory = newKeyword => {
    if (!newKeyword || newKeyword.length < 1 || newKeyword.length > 100) return
    const filtered = history.filter(k => k.toLowerCase() !== newKeyword.toLowerCase())
    setHistory([newKeyword, ...filtered].slice(0, 10))
  }

  const removeHistoryItem = idxToRemove => {
    setHistory(history.filter((_, idx) => idx !== idxToRemove))
  }

  const search = async (searchKeyword = keyword) => {
    const trimmedKeyword = searchKeyword.trim()
    if (!trimmedKeyword || trimmedKeyword.length < 1 || trimmedKeyword.length > 100) return

    try {
      setLoading(true)
      const publicationYears = uniq(publicationsToSearch?.map(publication => publication.year) || [])
      const searchYears = publicationYears.length ? publicationYears : [year]
      const responses = await Promise.all(
        searchYears.map(searchYear =>
          apiHook.get(
            `pubhub/publications/text-search?${qs.stringify({
              brand,
              model,
              modelType,
              year: searchYear,
              keyword: trimmedKeyword,
              language: language || 'en'
            })}`
          )
        )
      )
      const partNumbers = publicationsToSearch?.map(publication => publication.partNumber)
      const response = partNumbers?.length
        ? responses.flat().filter(item => item.type !== 'publication' || partNumbers.includes(item.title))
        : responses[0]
      setResult(response)
      updateHistory(trimmedKeyword)
      onSearchComplete?.({ keyword: trimmedKeyword, result: response })
    } catch (error) {
      console.error(error?.message)
    } finally {
      setLoading(false)
    }
  }

  const hasSearched = result !== null
  const hasResults = !!result?.length
  const numberOfResults = result?.length || 0

  const getHighlightedText = text => {
    if (keyword && typeof text === 'string') {
      const regex = new RegExp(`(${keyword})`, 'gi')
      return text.replace(regex, `<span class="${styles.highlight}">$1</span>`)
    }
    return text
  }

  const ResultItem = item => {
    const publicationLabel = usePublicationLabel(item?.publicationType)
    const resultItemTitle = item?.type === 'video' ? item?.title : item.title || t(publicationLabel)
    const handleClick = () => {
      if (item.type === 'video') {
        openVideoModal(item.title, item.url)
      } else {
        setActiveItem(item)
      }
    }

    return (
      <List.Item
        key={item.id}
        className={styles.resultItem}
        role="button"
        onClick={handleClick}
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
          }
        }}
      >
        <div className="flex-sb">
          <div>
            <div className="flex-l">
              {item.type === 'video' && <FaVideo className={cn('mr-10', styles.light)} />}
              {item.type === 'publication' && !!item.publicationType && (
                <SubaruPublicationTypeIcon
                  className={cn('mr-10', styles.light)}
                  name={item.icon || publicationTypes?.[item.publicationType]?.icon || 'vehicleOwnerManual'}
                  size={18}
                  aria-hidden="true"
                />
              )}
              <span className={styles.titleWithIcon}>
                <b>{resultItemTitle}</b>
              </span>
              {item.type === 'publication' && !!item.url && (
                <span className={cn('ml-10 mr-10', styles.light, styles.pdfIcon)}>
                  <GrDocumentPdf size={18} />
                </span>
              )}
            </div>
            {!!item.topics && (
              <div className={cn('small', 'mt-10', styles.light, styles.resultItemMatches)}>
                {item.topics.length} {t('search_matches', 'Matches')}
              </div>
            )}
          </div>
          <div className="flex-c">
            <IcoMoonIcon icon={ICON_MAP.chevronRight} className={styles.light} width={16} height={16} />
          </div>
        </div>
      </List.Item>
    )
  }

  const Topic = topic => {
    const redirectToTopic = url => {
      if (fullReloadOnSelectTopic) {
        window.location.href = url
      } else {
        router.push(url)
      }
    }

    const handleTopicClick = () => {
      const lang = language || 'en'
      let url = ''
      if (closeWrapper) closeWrapper()
      if (topic.page)
        url = `/pdf/${generatePartNumber(customerId)(activeItem, language)}?${qs.stringify({ page: topic.page, language: lang })}`
      else
        url = `/content?${qs.stringify({
          id: activeItem.persistentId,
          topicId: topic.persistentId,
          language: lang,
          publicationType: activeItem.publicationType
        })}`
      redirectToTopic(url)
    }

    return (
      <List.Item
        key={topic.id}
        className={styles.resultItem}
        role="button"
        onClick={handleTopicClick}
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleTopicClick()
          }
        }}
      >
        <div className="flex-sb">
          <div className={cn('flex-l', styles.topicMatchContent)}>
            <IcoMoonIcon
              icon={ICON_MAP.list}
              className={cn('mr-10', styles.light, styles.topicMatchIcon)}
              width={20}
              height={20}
              aria-hidden="true"
            />
            <div>
              <div className="flex-l">
                <b dangerouslySetInnerHTML={{ __html: getHighlightedText(sanitize(topic.title)) }} />
              </div>
              {!!topic.page && (
                <div className={cn('small', 'mt-10', styles.light)}>
                  {t('search_page', 'Page')} {topic.page}
                </div>
              )}
            </div>
          </div>
        </div>
      </List.Item>
    )
  }

  const keywordTrimmed = keyword.trim()
  const isDisableSearch = !keywordTrimmed || loading || keywordTrimmed.length < 1 || keywordTrimmed.length > 100

  const onClearKeyword = () => {
    setKeyword('')
    setResult(null)
  }

  const searchButton = (
    <InputGroup.Button
      onClick={() => {
        search()
        setShowHistory(false)
      }}
      disabled={isDisableSearch}
      aria-label={t('search_search', 'Search')}
      title={t('search_search', 'Search')}
    >
      {loading ? <Loader size="sm" /> : <BsSearch />}
    </InputGroup.Button>
  )

  return (
    <div className={styles.searchKeywordForm}>
      {!activeItem && (
        <div style={{ position: 'relative' }} ref={searchContainerRef}>
          <InputGroup inside className={styles.searchKeywordInputGroup}>
            {searchButton}
            <Input
              ref={searchInputRef}
              max={100}
              className={styles.searchKeywordInput}
              label={t('search_keyword', 'Keyword')}
              value={keyword}
              onChange={value => {
                setKeyword(value)
                if (value === '') setResult(null)
              }}
              placeholder={t('search_enter_keyword', 'Enter Keyword')}
              onFocus={() => setShowHistory(true)}
              onKeyDown={e => {
                if (e.key === 'Enter' && keywordTrimmed) {
                  search()
                  setShowHistory(false)
                }
                if (e.key === 'Escape') setShowHistory(false)
              }}
            />
            {keyword && (
              <InputGroup.Button
                aria-label={t('search_clear_input', 'Clear input')}
                title={t('search_clear_input', 'Clear input')}
                className={styles.clearInputBtn}
                onMouseDown={e => {
                  e.preventDefault()
                  onClearKeyword()
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onClearKeyword()
                  }
                }}
              >
                {t('search_clear', 'Clear')}
              </InputGroup.Button>
            )}
          </InputGroup>
          {showHistory && history.length > 0 && (
            <SearchByKeywordHistory
              history={history}
              t={t}
              onSelect={item => {
                setKeyword(item)
                setShowHistory(false)
                search(item)
              }}
              onRemove={removeHistoryItem}
              onClearAll={clearHistory}
            />
          )}
        </div>
      )}
      {!!activeItem && (
        <List bordered>
          <List.Item className={styles.backAndTitleRow}>
            <ActionButton appearance="ghost" onClick={() => setActiveItem(null)} className={styles.backBtn}>
              <DirectionalArrow direction="left" className="mr-10" />
              {t('search_to_search', 'Back to Search')}
            </ActionButton>
            <span className={styles.title}>{activeItem.title || t(activeItem.publicationType, activeItem.title)}</span>
          </List.Item>
        </List>
      )}
      {!!hasResults && (
        <>
          {!activeItem && (
            <div className={styles.resultsCount}>
              {numberOfResults} {t('search_results_for', 'results for')} &quot;{keywordTrimmed}&quot;
            </div>
          )}
          {activeItem?.topics?.length && (
            <div className={styles.resultsCount}>
              {activeItem.topics.length} {t('search_matches_for', 'matches for')} &quot;{keywordTrimmed}&quot;
            </div>
          )}
          <List bordered hover>
            {!activeItem && (
              <>
                {result?.map(item => (
                  <ResultItem key={item.id} {...item} />
                ))}
              </>
            )}
            {!!activeItem && (
              <>
                {activeItem.topics.map(item => (
                  <Topic key={item.id} {...item} />
                ))}
              </>
            )}
          </List>
        </>
      )}
      {hasSearched && !hasResults && !loading && (
        <div className={styles.resultsCount}>{t('search_no_results', 'No search results found')}</div>
      )}
      <VideoModal show={isVideoModalOpen} close={closeVideoModal} url={videoUrl} title={videoTitle} />
    </div>
  )
}

export default SearchByKeywordNordics
